import { PendingEdenredRecharge } from 'src/app/interfaces';

/**
 * Storage for a recharge interrupted by the one-off Edenred sign-in.
 *
 * This deliberately uses `localStorage`, not `sessionStorage`: `SessionStorageService`
 * registers a `beforeunload` handler that calls `sessionStorage.clear()`, so navigating away
 * to Edenred Connect would wipe the stash and the user would have to enter the amount again.
 *
 * A timestamp bounds how long the stash stays actionable. Without it, an abandoned sign-in
 * finished days later would silently charge the user for an amount they no longer expect.
 * The window mirrors the backend's pending-link TTL.
 */
const PENDING_EDENRED_RECHARGE_KEY = 'pendingEdenredRecharge';
const PENDING_EDENRED_RECHARGE_TTL_MS = 30 * 60 * 1000;

interface StoredPendingRecharge {
  recharge: PendingEdenredRecharge;
  storedAt: number;
}

/** Persist a recharge across the Edenred sign-in round trip. */
export function savePendingEdenredRecharge(recharge: PendingEdenredRecharge): void {
  const stored: StoredPendingRecharge = { recharge, storedAt: Date.now() };
  localStorage.setItem(PENDING_EDENRED_RECHARGE_KEY, JSON.stringify(stored));
}

/**
 * Read back the stashed recharge, or null when there is none, it is unreadable, or it has
 * aged out. Expired and malformed entries are cleared on read.
 */
export function readPendingEdenredRecharge(): PendingEdenredRecharge | null {
  const raw = localStorage.getItem(PENDING_EDENRED_RECHARGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const stored = JSON.parse(raw) as StoredPendingRecharge;
    if (!stored?.recharge || typeof stored.storedAt !== 'number') {
      clearPendingEdenredRecharge();
      return null;
    }
    if (Date.now() - stored.storedAt > PENDING_EDENRED_RECHARGE_TTL_MS) {
      clearPendingEdenredRecharge();
      return null;
    }
    return stored.recharge;
  } catch {
    clearPendingEdenredRecharge();
    return null;
  }
}

export function clearPendingEdenredRecharge(): void {
  localStorage.removeItem(PENDING_EDENRED_RECHARGE_KEY);
}
